/**
 * Pure helpers behind the post link preview: finding a URL in post body text,
 * deciding what kind of thing it points at, and turning fetched HTML into the
 * fields a preview card renders.
 *
 * Nothing here performs a network request. Fetching a stranger's URL is a
 * server-side concern and lives in the Cloudflare Worker under
 * `workers/link-preview`, which imports this module for the parsing half so the
 * app and the extractor can never disagree about what a page's metadata means.
 * The app reaches the extractor through
 * `src/services/api/link-preview/link-preview.service.ts`.
 */

import { isSafeImageUrl } from "./link-preview-safety.utils";

/**
 * Media kind for a URL, determined by extension sniffing.
 *
 * Detection method: URL pathname extension. A HEAD request for Content-Type
 * is more accurate but is CORS-gated in the browser (web-only target), so
 * extension sniffing is the only reliable option on web.
 *
 * - image / video → render directly without any metadata fetch
 * - page → hand to the extractor for provider/Open Graph metadata
 */
export type MediaKind = "image" | "video" | "page";

const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|svg|bmp|ico)(\?.*)?$/i;
const VIDEO_EXT_RE = /\.(mp4|mov|avi|webm|mkv|m4v|ogv|3gp)(\?.*)?$/i;

export const detectMediaKind = (url: string): MediaKind => {
  try {
    const { pathname } = new URL(url);
    if (IMAGE_EXT_RE.test(pathname)) return "image";
    if (VIDEO_EXT_RE.test(pathname)) return "video";
  } catch {
    // Malformed URL — treat as page
  }
  return "page";
};

// Matches http(s) URLs in a string. Recreated per call where the /g/ flag is
// needed, because a shared global regex carries `lastIndex` between callers.
const URL_PATTERN = /https?:\/\/(?:[-\w]+\.)+[\w]{2,}(?:\/[^\s]*)?/gi;

/**
 * Punctuation that reads as sentence structure rather than part of the address:
 * "Read https://example.com." links the URL and leaves the full stop behind.
 * Mirrors `linkify.utils`, so the card previews exactly the address the body
 * text turned into a link.
 */
const TRAILING_PUNCTUATION = /[.,;:!?'")\]}]+$/;

const trimTrailingPunctuation = (url: string): string =>
  url.replace(TRAILING_PUNCTUATION, "");

/** Every http(s) URL in `text`, in the order they appear. */
export const extractUrls = (text: string): string[] => {
  if (!text) return [];
  const pattern = new RegExp(URL_PATTERN.source, URL_PATTERN.flags);
  const urls: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    const url = trimTrailingPunctuation(match[0]);
    if (url) urls.push(url);
  }
  return urls;
};

/**
 * The URL a post previews, or null when it has none.
 *
 * A post can mention several links; like Facebook, only the first one gets a
 * card. Every URL stays in the body text as a tappable link regardless — the
 * preview is additional content, never a replacement.
 */
export const extractFirstUrl = (text: string): string | null =>
  extractUrls(text)[0] ?? null;

/** Hostname without `www.`, which is what the card's footer line shows. */
export const getDomain = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./i, "").toLowerCase();
  } catch {
    return url;
  }
};

/**
 * Cache key for a URL. Tracking parameters and the fragment are dropped so the
 * same article shared from two places resolves to one stored preview, but the
 * rest of the query string is kept — it usually selects the content.
 */
const TRACKING_PARAMS = [
  /^utm_/i,
  /^fbclid$/i,
  /^gclid$/i,
  /^mc_[ce]id$/i,
  /^igshid$/i,
  /^ref_src$/i,
];

export const normalizeUrl = (raw: string): string => {
  try {
    const url = new URL(raw.trim());
    url.hash = "";
    url.hostname = url.hostname.toLowerCase().replace(/\.$/, "");
    [...url.searchParams.keys()].forEach((key) => {
      if (TRACKING_PARAMS.some((pattern) => pattern.test(key))) {
        url.searchParams.delete(key);
      }
    });
    // A bare "/" adds nothing and splits the cache key needlessly.
    if (url.pathname === "/" && !url.search) return url.origin;
    return url.toString();
  } catch {
    return raw.trim();
  }
};

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

export const decodeHtmlEntities = (value: string): string =>
  value
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(parseInt(dec, 10)))
    .replace(
      /&([a-z]+);/gi,
      (match, name) => NAMED_ENTITIES[name.toLowerCase()] ?? match,
    );

/** Control and zero-width characters a page can hide inside a meta `content`. */
const INVISIBLE_CHARS = /[\u0000-\u001F\u007F\u200B-\u200F\u2028\u2029\uFEFF]/g;

/**
 * Make a metadata string safe to store and render: decode entities, drop any
 * markup and control characters the page smuggled into a `content` attribute,
 * collapse whitespace, and clamp the length so one hostile page cannot bloat a
 * stored row or the feed layout.
 */
export const sanitizeMetaText = (
  value: string | null | undefined,
  maxLength = 300,
): string | null => {
  if (typeof value !== "string") return null;
  const cleaned = decodeHtmlEntities(value)
    .replace(/<[^>]*>/g, " ")
    .replace(INVISIBLE_CHARS, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return null;
  return cleaned.length > maxLength
    ? `${cleaned.slice(0, maxLength).trim()}…`
    : cleaned;
};

/**
 * Resolve a metadata image against the page it came from and refuse it unless
 * it survives the same host rules as the page itself — an `og:image` pointing
 * at `http://169.254.169.254/...` must not reach the client.
 */
export const sanitizeImageUrl = (
  value: string | null | undefined,
  baseUrl: string,
): string | null => {
  if (typeof value !== "string" || !value.trim()) return null;
  let absolute: string;
  try {
    absolute = new URL(decodeHtmlEntities(value.trim()), baseUrl).toString();
  } catch {
    return null;
  }
  return isSafeImageUrl(absolute) ? absolute : null;
};

/**
 * Read a `<meta>` value, trying each key in order and accepting whichever of
 * `property` / `name` / `itemprop` the page used, with the attributes in either
 * order.
 */
const readMeta = (html: string, keys: readonly string[]): string | null => {
  for (const key of keys) {
    const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const attr = "(?:property|name|itemprop)";
    const propertyFirst = html.match(
      new RegExp(
        `<meta[^>]+${attr}=["']\\s*${escaped}\\s*["'][^>]*content=["']([^"']*)["']`,
        "i",
      ),
    );
    const contentFirst = html.match(
      new RegExp(
        `<meta[^>]+content=["']([^"']*)["'][^>]*${attr}=["']\\s*${escaped}\\s*["']`,
        "i",
      ),
    );
    const value = propertyFirst?.[1] ?? contentFirst?.[1];
    if (value && value.trim()) return value;
  }
  return null;
};

export type HtmlMetadata = {
  title: string | null;
  description: string | null;
  imageUrl: string | null;
  siteName: string | null;
  canonicalUrl: string | null;
};

/**
 * Extract preview metadata from a page, preferring Open Graph, then Twitter
 * cards, then the plain HTML `<title>` / `<meta name="description">`. Every
 * field degrades on its own: a page with no `og:image` still yields a title.
 */
export const parseHtmlMetadata = (html: string): HtmlMetadata => {
  if (typeof html !== "string" || !html) {
    return {
      title: null,
      description: null,
      imageUrl: null,
      siteName: null,
      canonicalUrl: null,
    };
  }

  const titleTag = html.match(/<title[^>]*>([\s\S]*?)<\/title>/i)?.[1] ?? null;

  return {
    title:
      readMeta(html, ["og:title", "twitter:title"]) ??
      (titleTag ? titleTag.trim() : null),
    description: readMeta(html, [
      "og:description",
      "twitter:description",
      "description",
    ]),
    imageUrl: readMeta(html, [
      "og:image:secure_url",
      "og:image:url",
      "og:image",
      "twitter:image",
      "twitter:image:src",
    ]),
    siteName: readMeta(html, ["og:site_name", "application-name"]),
    canonicalUrl: readMeta(html, ["og:url"]),
  };
};

/**
 * True when a post body is nothing but `url` (markdown/angle-bracket link syntax tolerated).
 * Such a post shows just the preview card, like the Selisian app.
 */
export const isOnlyUrl = (text: string, url: string): boolean => {
  const trimmed = (text ?? "").trim();
  return trimmed === url || trimmed === `<${url}>` || trimmed === `[${url}](${url})`;
};

/** Card footer: "Site · domain", without repeating a site name that is just the domain. */
export const previewSourceLabel = (preview: {
  siteName: string | null;
  domain: string;
}): string =>
  preview.siteName && preview.siteName.toLowerCase() !== preview.domain.toLowerCase()
    ? `${preview.siteName} · ${preview.domain}`
    : preview.domain;

/**
 * The site's favicon. The `linkpreview` schema has no icon column, so (as in the app)
 * it is derived from the link's origin; the card hides it if it fails to load.
 */
export const previewFaviconUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;
    return `${parsed.protocol}//${parsed.host}/favicon.ico`;
  } catch {
    return null;
  }
};
